/**
 * Bookmark management logic for study questions
 * Shared between web and mobile applications
 */

/**
 * Checks if a question is bookmarked
 * @param {number} questionId - The question ID to check
 * @param {number[]} bookmarks - Array of bookmarked question IDs
 * @returns {boolean} True if the question is bookmarked
 */
export const isQuestionBookmarked = (questionId, bookmarks) => {
    if (!bookmarks || !Array.isArray(bookmarks)) {
        return false;
    }
    return bookmarks.includes(questionId);
};

/**
 * Adds a question to bookmarks
 * @param {number} questionId - The question ID to bookmark
 * @param {number[]} currentBookmarks - Current array of bookmarked question IDs
 * @returns {number[]} Updated bookmarks array
 */
export const addQuestionToBookmarks = (questionId, currentBookmarks = []) => {
    if (currentBookmarks.includes(questionId)) {
        return currentBookmarks; // Already bookmarked
    }
    return [...currentBookmarks, questionId];
};

/**
 * Removes a question from bookmarks
 * @param {number} questionId - The question ID to remove from bookmarks
 * @param {number[]} currentBookmarks - Current array of bookmarked question IDs
 * @returns {number[]} Updated bookmarks array
 */
export const removeQuestionFromBookmarks = (questionId, currentBookmarks = []) => {
    return currentBookmarks.filter(id => id !== questionId);
};

/**
 * Toggles bookmark status for a question
 * @param {number} questionId - The question ID to toggle
 * @param {number[]} currentBookmarks - Current array of bookmarked question IDs
 * @returns {Object} Object containing updated bookmarks and action taken
 */
export const toggleQuestionBookmark = (questionId, currentBookmarks = []) => {
    const isCurrentlyBookmarked = isQuestionBookmarked(questionId, currentBookmarks);

    if (isCurrentlyBookmarked) {
        return {
            bookmarks: removeQuestionFromBookmarks(questionId, currentBookmarks),
            action: 'removed',
            isBookmarked: false
        };
    } else {
        return {
            bookmarks: addQuestionToBookmarks(questionId, currentBookmarks),
            action: 'added',
            isBookmarked: true
        };
    }
};

/**
 * Gets bookmark statistics
 * @param {number[]} bookmarks - Array of bookmarked question IDs
 * @param {Object[]} questionsList - Full list of questions
 * @returns {Object} Bookmark statistics
 */
export const getBookmarkStatistics = (bookmarks = [], questionsList = []) => {
    const totalQuestions = questionsList.length;
    const bookmarkedCount = bookmarks.length;
    const bookmarkPercentage = totalQuestions > 0 ? (bookmarkedCount / totalQuestions) * 100 : 0;

    return {
        totalQuestions,
        bookmarkedCount,
        bookmarkPercentage: Math.round(bookmarkPercentage * 100) / 100, // Round to 2 decimal places
        unbookmarkedCount: totalQuestions - bookmarkedCount
    };
};